import React, { useMemo, useState } from 'react';
import { ChevronLeft, Info } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { FileUpload } from '../ui/FileUpload';
import { MALAYSIAN_BANK_OPTIONS } from '../../constants/malaysia';
import { DemoFillBanner } from '../ui/DemoFillBanner';
import { buildSampleBankAccount } from '../../constants/sampleData';
interface BankAccountJourneyProps {
  onComplete: (data: BankAccountData) => void;
  onExit: () => void;
  initialData?: Partial<BankAccountData>;
}
export interface BankAccountData {
  bank: string;
  accountNumber: string;
  bankStatement: File | null;
}
export function BankAccountJourney({
  onComplete,
  onExit,
  initialData
}: BankAccountJourneyProps) {
  const [bank, setBank] = useState<string>(initialData?.bank || 'Maybank');
  const [accountNumber, setAccountNumber] = useState(initialData?.accountNumber || '');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState(initialData?.accountNumber || '');
  const [bankStatement, setBankStatement] = useState<File | null>(initialData?.bankStatement || null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const sanitizeDigits = (raw: string) => raw.replace(/\D/g, '').slice(0, 20);
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!bank) e.bank = 'Please select your bank.';
    if (!accountNumber) {
      e.accountNumber = 'Account Number is required.';
    } else if (accountNumber.length < 8) {
      e.accountNumber = 'Account Number must be at least 8 digits.';
    }
    if (!confirmAccountNumber) {
      e.confirmAccountNumber = 'Please confirm your Account Number.';
    } else if (confirmAccountNumber !== accountNumber) {
      e.confirmAccountNumber = 'Account numbers do not match.';
    }
    if (!bankStatement) {
      e.bankStatement = 'Please upload a recent bank statement.';
    }
    return e;
  }, [bank, accountNumber, confirmAccountNumber, bankStatement]);
  const isValid = Object.keys(errors).length === 0;
  const showError = (key: string) => submitAttempted ? errors[key] : undefined;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!isValid) return;
    onComplete({
      bank,
      accountNumber,
      bankStatement
    });
  };
  return <div className="flex flex-col h-full max-w-xl mx-auto pt-2 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-96">
      {/* Back link */}
      <button type="button" onClick={onExit} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 mb-6 self-start focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 rounded" data-id="element-97">
        <ChevronLeft className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-98" />
        Back to overview
      </button>

      {/* Header */}
      <header className="mb-6" data-id="element-99">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 leading-tight" data-id="element-100">
          Link your bank account
        </h1>
        <p className="text-base text-gray-600 leading-relaxed" data-id="element-101">
          Where should we send your payouts? We'll send your earnings directly
          to this account.
        </p>
      </header>

      <DemoFillBanner className="mb-6" onFill={() => {
      const sampleData = buildSampleBankAccount();
      setBank(sampleData.bank);
      setAccountNumber(sampleData.accountNumber);
      setConfirmAccountNumber(sampleData.accountNumber);
      setBankStatement(sampleData.bankStatement);
    }} data-id="element-102" />

      {/* Info banner */}
      <div role="note" className="mb-8 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4" data-id="element-103">
        <Info className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-104" />
        <p className="text-sm text-blue-900 leading-relaxed" data-id="element-105">
          <span className="font-semibold" data-id="element-106">Important:</span> To avoid payout
          delays, your bank account name must exactly match your Legal Business
          Name. For Sole Proprietors, personal accounts matching your MyKad or
          Passport are accepted.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 flex flex-col" noValidate data-id="element-107">
        <div className="space-y-5" data-id="element-108">
          <Select label="Select your bank" options={MALAYSIAN_BANK_OPTIONS} value={bank} onChange={e => setBank(e.target.value)} error={showError('bank')} required data-id="element-109" />

          <Input label="Account Number" inputMode="numeric" autoComplete="off" placeholder="e.g. 1234567890123" value={accountNumber} onChange={e => setAccountNumber(sanitizeDigits(e.target.value))} error={showError('accountNumber')} helperText="Numeric only. Length varies by bank (typically 8–16 digits)." required data-id="element-110" />

          <Input label="Confirm Account Number" inputMode="numeric" autoComplete="off" placeholder="Re-enter your account number" value={confirmAccountNumber} onChange={e => setConfirmAccountNumber(sanitizeDigits(e.target.value))} onPaste={e => {
          // Discourage paste to prevent typo carry-over
          e.preventDefault();
        }} error={showError('confirmAccountNumber')} helperText="For your security, please re-type the number rather than paste." required data-id="element-111" />

          <div className="pt-4 border-t border-gray-100" data-id="element-112">
            <h3 className="text-base font-bold uppercase tracking-wider text-gray-900 mb-1.5" data-id="element-113">
              Financial Proof
            </h3>
            <p className="text-sm text-gray-600 mb-5" data-id="element-114">
              Helps us match payouts to the correct account.
            </p>
            <FileUpload label="Recent Bank Statement" description="Must show account name and number matching the details above." value={bankStatement} onChange={setBankStatement} error={showError('bankStatement')} required data-id="element-115" />
          </div>
        </div>

        {/* Sticky footer */}
        <div className="sticky bottom-0 left-0 right-0 -mx-2 mt-12 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-10" data-id="element-116">
          <div className="flex items-center justify-between gap-3 py-4 px-2" data-id="element-117">
            <Button variant="outline" type="button" onClick={onExit} data-id="element-118">
              Back
            </Button>
            <Button type="submit" disabled={!isValid} data-id="element-119">
              Save & Continue
            </Button>
          </div>
        </div>
      </form>
    </div>;
}